import { Card, Muted, Stack, TrendChart, color, radius, space, status } from '@biopeak/ui';
import { useMemo, useState } from 'react';
import { formatElapsed, toTrendDisplayUnit } from '../lib/format.ts';
import {
  useMetricCatalog,
  usePatientOverview,
  usePatientTimeline,
  usePatientTrend,
  type TimelineEvent,
  type TrafficLight,
} from '../lib/patient.ts';
import { OverviewCards } from './OverviewCards.tsx';

/**
 * Aba "Visão geral" do perfil: resumo, tendência e linha do tempo (§4.2).
 *
 * Separada do `PatientProfile` quando o perfil ganhou abas — o conteúdo é o
 * mesmo de antes, movido sem alteração de comportamento. O que motivou a
 * separação é o §8.2 princípio 3: uma ideia por view. Empilhar comunicação e
 * regras de alerta nesta mesma tela colocaria três assuntos concorrentes
 * disputando a atenção do médico ao mesmo tempo.
 */

const PERIODS = [
  { days: 7, label: '7 dias' },
  { days: 30, label: '30 dias' },
  { days: 90, label: '90 dias' },
];

const STATUS_LABEL: Record<TrafficLight, string> = {
  red: 'Vermelho',
  yellow: 'Amarelo',
  green: 'Verde',
};

export function PatientOverview({ patientId }: { patientId: string }) {
  const [metricKey, setMetricKey] = useState('heart_rate_daily_max');
  const [days, setDays] = useState(30);
  // Período dos cards de resumo, separado do período do gráfico: o médico
  // costuma querer o panorama de 90 dias enquanto olha a tendência da última
  // semana, e amarrar os dois obrigaria a desfazer uma leitura para fazer a
  // outra.
  const [overviewDays, setOverviewDays] = useState(30);

  const overview = usePatientOverview(patientId, overviewDays);
  const catalog = useMetricCatalog();
  const trend = usePatientTrend(patientId, metricKey, days);
  const timeline = usePatientTimeline(patientId);

  const metric = catalog.data?.find((m) => m.metric_key === metricKey);

  const points = useMemo(
    () =>
      (trend.data ?? []).map((r) => ({
        bucket: r.bucket,
        min: Number(r.min_value),
        max: Number(r.max_value),
        avg: Number(r.avg_value),
      })),
    [trend.data],
  );

  const hasBand = points.length > 1 && points.some((p) => p.max > p.min);

  const trendDisplay = useMemo(
    () => toTrendDisplayUnit(metric?.canonical_unit, points),
    [metric?.canonical_unit, points],
  );

  return (
    <>
      {/* ------------------------------------------------- visão geral */}
      <Stack gap={space.md}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: space.md,
            flexWrap: 'wrap',
          }}
        >
          <h2 style={{ margin: 0, fontSize: '19px' }}>Visão geral</h2>
          <div style={{ display: 'flex', gap: space.sm }}>
            {PERIODS.map((p) => (
              <PeriodPill
                key={p.days}
                label={p.label}
                active={overviewDays === p.days}
                onClick={() => setOverviewDays(p.days)}
              />
            ))}
          </div>
        </div>

        {overview.isError ? (
          <Card>
            <Muted style={{ color: status.red.fg }}>Não foi possível carregar o resumo.</Muted>
          </Card>
        ) : overview.isLoading || !overview.data ? (
          <Card>
            <Muted>Carregando…</Muted>
          </Card>
        ) : (
          <OverviewCards data={overview.data} patientId={patientId} />
        )}
      </Stack>

      {/* ---------------------------------------------------- tendência */}
      <Card>
        <Stack gap={space.md}>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: space.md,
              flexWrap: 'wrap',
            }}
          >
            <h2 style={{ margin: 0, fontSize: '19px' }}>Tendência</h2>
            {/* `minWidth: 0` no wrapper e nos dois selects: sem isto, o select
                de métrica (nomes de exame longos, ex. "Anticorpo
                antiperoxidase tireoidiana") vaza a largura da tela no celular
                — o mesmo bug de `min-width: auto` do flex já visto no campo
                de mensagem do chat do paciente. */}
            <div style={{ display: 'flex', gap: space.sm, flexWrap: 'wrap', minWidth: 0, width: '100%' }}>
              <select
                value={metricKey}
                onChange={(e) => setMetricKey(e.target.value)}
                style={{ ...selectStyle, flex: '1 1 200px' }}
              >
                {(catalog.data ?? []).map((m) => (
                  <option key={m.metric_key} value={m.metric_key}>
                    {m.display_name}
                  </option>
                ))}
              </select>
              <select
                value={days}
                onChange={(e) => setDays(Number(e.target.value))}
                style={selectStyle}
              >
                {PERIODS.map((p) => (
                  <option key={p.days} value={p.days}>
                    {p.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {trend.isError ? (
            <Muted style={{ color: status.red.fg }}>Não foi possível carregar essa métrica.</Muted>
          ) : trend.isLoading ? (
            <Muted>Carregando…</Muted>
          ) : (
            <TrendChart
              points={trendDisplay.points}
              unit={trendDisplay.unit}
              formatValue={trendDisplay.formatValue}
            />
          )}

          {/* A faixa é a informação, não decoração — explicar uma vez evita
              que ela seja lida como "erro de medição".
              Mas só quando ela EXISTE: métricas que já são agregados diários
              (`FC máxima do dia`, `peso`) têm min = máx = média, a faixa
              colapsa na linha, e a legenda estaria descrevendo algo que o
              médico não vê na tela. */}
          {hasBand && (
            <Muted style={{ fontSize: '12px' }}>
              A linha é a média do dia; a faixa é a variação entre o menor e o maior valor
              registrado naquele dia.
            </Muted>
          )}
        </Stack>
      </Card>

      {/* ------------------------------------------------- linha do tempo */}
      <Card>
        <Stack gap={space.md}>
          <h2 style={{ margin: 0, fontSize: '19px' }}>Linha do tempo</h2>
          {timeline.isLoading && <Muted>Carregando…</Muted>}
          {timeline.data?.length === 0 && <Muted>Nenhum evento registrado ainda.</Muted>}
          <Stack gap={space.md}>
            {(timeline.data ?? []).map((event, i) => (
              <TimelineItem key={`${event.kind}-${event.occurred_at}-${i}`} event={event} />
            ))}
          </Stack>
        </Stack>
      </Card>
    </>
  );
}

// -------------------------------------------------------- linha do tempo

function TimelineItem({ event }: { event: TimelineEvent }) {
  const kind = event.kind ?? '';

  let title: string;
  // Anotação explícita: os tokens são `as const`, então sem ela o TS infere o
  // literal '#D2D2D7' e recusa qualquer outra cor nas atribuições abaixo.
  let dot: string = color.border;

  if (kind === 'status_change') {
    const to = (event.detail_key ?? 'green') as TrafficLight;
    const from = event.previous_key as TrafficLight | null;
    dot = status[to].fg;
    title = from
      ? `${STATUS_LABEL[from]} → ${STATUS_LABEL[to]}`
      : `Estado inicial: ${STATUS_LABEL[to]}`;
    if (event.is_manual_override) title += ' (manual)';
  } else if (kind === 'feed_sent') {
    title = 'Comunicado enviado';
    dot = color.accent;
  } else if (kind === 'feed_acknowledged') {
    title = 'Paciente confirmou a leitura';
    dot = status.green.fg;
  } else if (kind === 'chat_patient') {
    title = 'Mensagem do paciente';
    dot = color.textSecondary;
  } else if (kind === 'chat_doctor') {
    title = 'Mensagem sua';
    dot = color.textSecondary;
  } else {
    title = kind;
  }

  return (
    <div style={{ display: 'flex', gap: space.sm }}>
      <span
        aria-hidden
        style={{
          width: 8,
          height: 8,
          borderRadius: '50%',
          background: dot,
          marginTop: 6,
          flexShrink: 0,
        }}
      />
      <Stack gap={space.xs} style={{ minWidth: 0 }}>
        <div style={{ display: 'flex', gap: space.sm, alignItems: 'baseline', flexWrap: 'wrap' }}>
          <span style={{ fontSize: '15px', fontWeight: 500 }}>{title}</span>
          <Muted style={{ fontSize: '12px' }}>
            {formatElapsed(event.occurred_at)} · {event.actor_name}
          </Muted>
        </div>
        {event.body && (
          <Muted style={{ fontSize: '13px', whiteSpace: 'pre-wrap' }}>{event.body}</Muted>
        )}
      </Stack>
    </div>
  );
}

function PeriodPill({
  label,
  active,
  onClick,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      style={{
        border: `1px solid ${active ? color.accent : color.border}`,
        background: active ? `${color.accent}12` : color.white,
        color: active ? color.accent : color.text,
        borderRadius: radius.pill,
        padding: '6px 14px',
        fontSize: '13px',
        fontWeight: active ? 600 : 400,
        cursor: 'pointer',
      }}
    >
      {label}
    </button>
  );
}

// `appearance: none` + seta própria via SVG: sem isto, o iOS Safari desenha
// o indicador nativo (dois carets empilhados) por cima do pill customizado —
// no aparelho real ficava um ícone estranho, meio duplicado, que não combina
// com o resto dos ícones do produto (todos SVG traço fino, próprios).
const selectStyle: React.CSSProperties = {
  border: `1px solid ${color.border}`,
  borderRadius: radius.pill,
  padding: '6px 30px 6px 12px',
  fontSize: '13px',
  background: `${color.white} url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none'%3E%3Cpath d='M6 9l6 6 6-6' stroke='%23616161' stroke-width='1.8' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E") no-repeat right 10px center`,
  backgroundSize: '16px',
  color: color.text,
  cursor: 'pointer',
  appearance: 'none',
  WebkitAppearance: 'none',
  MozAppearance: 'none',
  maxWidth: '100%',
  minWidth: 0,
};
